import { publishRemote, subscribeRemote } from "shared/services/recordMutationBroadcast";

export type RecordMutationOperation = "create" | "update" | "delete" | "unknown";

export type RecordMutationEvent = {
    table: string;
    operation?: RecordMutationOperation;
    ids?: string[];
    source?: string;
};

type Listener = (event: RecordMutationEvent) => void;

const listeners = new Set<Listener>();

const normalizeTableName = (table: string) => table.trim();

const dispatchLocal = (event: RecordMutationEvent) => {
    listeners.forEach(listener => listener(event));
};

subscribeRemote(dispatchLocal);

export const emitRecordMutation = (event: RecordMutationEvent) => {
    const table = normalizeTableName(event.table);

    if (!table) {
        return;
    }

    const normalized = {
        ...event,
        table,
        operation: event.operation || "unknown",
    };

    dispatchLocal(normalized);
    publishRemote(normalized);
};

export const emitRecordMutations = (events: RecordMutationEvent[]) => {
    events.forEach(emitRecordMutation);
};

export const createRecordMutationCollector = (source?: string) => {
    const eventsByTable = new Map<string, RecordMutationEvent>();
    const touchedTables = new Set<string>();

    const mark = (event: string | RecordMutationEvent) => {
        const resolvedEvent = typeof event === "string" ? { table: event } : event;
        const table = normalizeTableName(resolvedEvent.table);

        if (!table) {
            return;
        }

        touchedTables.add(table);

        const current = eventsByTable.get(table);
        const ids = [
            ...(current?.ids || []),
            ...(resolvedEvent.ids || []),
        ].filter(Boolean);

        eventsByTable.set(table, {
            ...current,
            ...resolvedEvent,
            table,
            operation: resolvedEvent.operation || current?.operation || "unknown",
            ids: ids.length > 0 ? Array.from(new Set(ids)) : undefined,
            source: resolvedEvent.source || current?.source || source,
        });
    };

    const markMany = (events: Array<string | RecordMutationEvent>) => {
        events.forEach(mark);
    };

    const flush = () => {
        emitRecordMutations(Array.from(eventsByTable.values()));
        eventsByTable.clear();
    };

    return {
        mark,
        markMany,
        flush,
        get pendingTables() {
            return Array.from(eventsByTable.keys());
        },
        get touchedTables() {
            return Array.from(touchedTables);
        },
    };
};

export const recordMutationBus = {
    emit(event: RecordMutationEvent) {
        emitRecordMutation(event);
    },
    subscribe(listener: Listener) {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }
};
